import { isEqual } from 'lodash';
import APP_CONFIG from '@/configs/appConfig';
import { ProjectRenderer } from '../ProjectRenderer';
import { ThreeEvent } from '../types';
import { SerializedModulesManager } from './ModulesManager';

export type ModulesHistorySnapshot = SerializedModulesManager;

export class ModulesHistoryManager {
	private snapshots: ModulesHistorySnapshot[] = [];
	private currentIndex = -1;
	private _isRestoring = false;
	private pendingGroupBefore: ModulesHistorySnapshot | null = null;

	constructor(private renderer: ProjectRenderer) {}

	get isRestoring() {
		return this._isRestoring;
	}

	get hasPendingGroupTransaction() {
		return this.pendingGroupBefore !== null;
	}

	private get modulesManager() {
		return this.renderer.modulesManager;
	}

	private async restoreAt(index: number) {
		const snapshot = this.snapshots[index];
		if (!snapshot) return;

		this._isRestoring = true;

		try {
			await this.modulesManager.restoreFromSnapshot(snapshot);
			this.currentIndex = index;
			this.emitHistoryUpdate();
		} finally {
			this._isRestoring = false;
		}
	}

	private trimToLimit() {
		const limit = APP_CONFIG.CREATOR_MODULES_HISTORY_LIMIT;
		while (this.snapshots.length > limit) {
			this.snapshots.shift();
			this.currentIndex = Math.max(0, this.currentIndex - 1);
		}
	}

	private emitHistoryUpdate() {
		this.renderer.emitter.emit(ThreeEvent.MODULES_HISTORY, {
			historyLength: this.snapshots.length,
			currentHistoryIndex: this.currentIndex,
		});
	}

	capture() {
		return structuredClone(
			this.modulesManager.export()
		) as ModulesHistorySnapshot;
	}

	commit(before: ModulesHistorySnapshot) {
		if (this._isRestoring) return;
		if (this.pendingGroupBefore) return;

		const after = this.capture();
		if (isEqual(before, after)) return;

		if (this.currentIndex < this.snapshots.length - 1)
			this.snapshots.splice(this.currentIndex + 1);

		if (!this.snapshots.length) this.snapshots.push(structuredClone(before));

		this.snapshots.push(after);
		this.trimToLimit();
		this.currentIndex = this.snapshots.length - 1;
		this.emitHistoryUpdate();
	}

	beginGroupTransaction(before?: ModulesHistorySnapshot) {
		if (this._isRestoring) return;
		this.pendingGroupBefore = before ?? this.capture();
	}

	commitGroupTransaction() {
		if (this._isRestoring) return;
		if (!this.pendingGroupBefore) return;

		const before = this.pendingGroupBefore;
		this.pendingGroupBefore = null;
		this.commit(before);
	}

	cancelGroupTransaction() {
		this.pendingGroupBefore = null;
	}

	async undo() {
		if (this._isRestoring) return;
		if (this.pendingGroupBefore) return;
		if (this.currentIndex < 1) return;

		await this.restoreAt(this.currentIndex - 1);
	}

	async redo() {
		if (this._isRestoring) return;
		if (this.pendingGroupBefore) return;
		if (this.currentIndex >= this.snapshots.length - 1) return;

		await this.restoreAt(this.currentIndex + 1);
	}

	reset() {
		this.pendingGroupBefore = null;
		this.snapshots = [];
		this.currentIndex = -1;
		this.emitHistoryUpdate();
	}
}
